import type { ComponentProps, HTMLAttributes, ReactNode } from 'react';
import { createContext, forwardRef, useCallback, useContext, useMemo } from 'react';
import { FileUIPart } from '../PromptInput';
import { ImageIcon } from '@WebComponents/svgs/icons/ImageIcon';
import { Music2Icon } from '@WebComponents/svgs/icons/Music2Icon';
import { FileTextIcon } from '@WebComponents/svgs/icons/FileTextIcon';
import { GlobeIcon } from '@WebComponents/svgs/icons/GlobeIcon';
import { PaperClipIcon } from '@WebComponents/svgs/icons/PaperClipIcon';
import { VideoIcon } from '@WebComponents/svgs/icons/VideoIcon';
import { css, StyleSheet } from 'aphrodite';
import { antiFlashWhite, background, darkGrayFontColor, inputBorderColor, titles, white } from '@Styles/colors';
import { Button } from '@HoudiniUI/Button';
import { XIconNew } from '@WebComponents/svgs/icons/XIcon';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@WebComponents/HoverCard';

// ============================================================================
// Types
// ============================================================================

export type AttachmentData = FileUIPart & { id: string };

export type AttachmentMediaCategory = 'image' | 'video' | 'audio' | 'document' | 'source' | 'unknown';

export type AttachmentVariant = 'grid' | 'inline' | 'list';

const mediaCategoryIcons: Record<AttachmentMediaCategory, typeof ImageIcon> = {
	audio: Music2Icon,
	document: FileTextIcon,
	image: ImageIcon,
	source: GlobeIcon,
	unknown: PaperClipIcon,
	video: VideoIcon,
};

// ============================================================================
// Utility Functions
// ============================================================================

export const getMediaCategory = (data: AttachmentData): AttachmentMediaCategory => {
	const mediaType = data.mediaType ?? '';

	if (mediaType.startsWith('image/')) {
		return 'image';
	}
	if (mediaType.startsWith('video/')) {
		return 'video';
	}
	if (mediaType.startsWith('audio/')) {
		return 'audio';
	}
	if (mediaType.startsWith('application/') || mediaType.startsWith('text/')) {
		return 'document';
	}

	return 'unknown';
};

export const getAttachmentLabel = (data: AttachmentData): string => {
	const category = getMediaCategory(data);
	return data.filename || (category === 'image' ? 'Image' : 'Attachment');
};

const renderAttachmentImage = (url: string, filename: string | undefined, isGrid: boolean) =>
	isGrid ? (
		<img alt={filename || 'Image'} className={css(s.attachmentImage)} height={96} src={url} width={96} />
	) : (
		<img alt={filename || 'Image'} className={css(s.attachmentImageRounded)} height={20} src={url} width={20} />
	);

// ============================================================================
// Contexts
// ============================================================================

interface AttachmentsContextValue {
	variant: AttachmentVariant;
}

const AttachmentsContext = createContext<AttachmentsContextValue | null>(null);

interface AttachmentContextValue {
	data: AttachmentData;
	mediaCategory: AttachmentMediaCategory;
	onRemove?: () => void;
	variant: AttachmentVariant;
}

const AttachmentContext = createContext<AttachmentContextValue | null>(null);

// ============================================================================
// Hooks
// ============================================================================

export const useAttachmentsContext = () => useContext(AttachmentsContext) ?? { variant: 'grid' as const };

export const useAttachmentContext = () => {
	const ctx = useContext(AttachmentContext);
	if (!ctx) {
		throw new Error('Attachment components must be used within <Attachment>');
	}
	return ctx;
};

// ============================================================================
// Attachments - Container
// ============================================================================

export type AttachmentsProps = AphroditeStyled<
	HTMLAttributes<HTMLDivElement> & {
		variant?: AttachmentVariant;
	}
>;

export const Attachments = ({ variant = 'grid', styles = [], children, ...props }: AttachmentsProps) => {
	const contextValue = useMemo(() => ({ variant }), [variant]);

	return (
		<AttachmentsContext.Provider value={contextValue}>
			<div
				className={css(
					s.attachments,
					variant === 'list' ? s.attachmentsList : s.attachmentsNonList,
					variant === 'grid' && s.attachmentsGrid,
					...styles
				)}
				{...props}
			>
				{children}
			</div>
		</AttachmentsContext.Provider>
	);
};

// ============================================================================
// Attachment - Item
// ============================================================================

export type AttachmentProps = AphroditeStyled<
	HTMLAttributes<HTMLDivElement> & {
		data: AttachmentData;
		onRemove?: () => void;
	}
>;

export const Attachment = forwardRef(
	({ data, onRemove, styles = [], children, ...props }: AttachmentProps, ref: React.Ref<HTMLDivElement>) => {
		const { variant } = useAttachmentsContext();
		const mediaCategory = getMediaCategory(data);

		const contextValue = useMemo<AttachmentContextValue>(
			() => ({ data, mediaCategory, onRemove, variant }),
			[data, mediaCategory, onRemove, variant]
		);

		return (
			<AttachmentContext.Provider value={contextValue}>
				<div
					className={css(
						s.attachment,
						variant === 'grid' && s.attachmentGrid,
						variant === 'inline' && s.attachmentInline,
						variant === 'list' && s.attachmentList,
						...styles
					)}
					ref={ref}
					{...props}
				>
					{children}
				</div>
			</AttachmentContext.Provider>
		);
	}
);

// ============================================================================
// AttachmentPreview - Media preview
// ============================================================================

export type AttachmentPreviewProps = AphroditeStyled<
	HTMLAttributes<HTMLDivElement> & {
		fallbackIcon?: ReactNode;
	}
>;

export const AttachmentPreview = ({ fallbackIcon, styles = [], ...props }: AttachmentPreviewProps) => {
	const { data, mediaCategory, variant } = useAttachmentContext();

	const iconSizeStyle = variant === 'inline' ? s.attachmentPreviewIconInline : s.attachmentPreviewIconNonInline;

	const renderIcon = (Icon: typeof ImageIcon) => <Icon className={css(s.attachmentPreviewIcon, iconSizeStyle)} />;

	const renderContent = () => {
		if (mediaCategory === 'image' && data.type === 'file' && data.url) {
			return renderAttachmentImage(data.url, data.filename, variant === 'grid');
		}

		if (mediaCategory === 'video' && data.type === 'file' && data.url) {
			return <video className={css(s.attachmentVideo)} muted src={data.url} />;
		}

		const Icon = mediaCategoryIcons[mediaCategory];
		return fallbackIcon ?? renderIcon(Icon);
	};

	return (
		<div
			className={css(
				s.attachmentPreview,
				variant === 'grid' && s.attachmentPreviewGrid,
				variant === 'inline' && s.attachmentPreviewInline,
				variant === 'list' && s.attachmentPreviewList,
				...styles
			)}
			{...props}
		>
			{renderContent()}
		</div>
	);
};

// ============================================================================
// AttachmentInfo - Name and type display
// ============================================================================

export type AttachmentInfoProps = AphroditeStyled<
	HTMLAttributes<HTMLDivElement> & {
		showMediaType?: boolean;
	}
>;

export const AttachmentInfo = ({ showMediaType = false, styles = [], ...props }: AttachmentInfoProps) => {
	const { data, variant } = useAttachmentContext();
	const label = getAttachmentLabel(data);

	if (variant === 'grid') {
		return null;
	}

	return (
		<div className={css(s.attachmentInfo, ...styles)} {...props}>
			<span className={css(s.attachmentInfoLabel)}>{label}</span>
			{showMediaType && data.mediaType && <span className={css(s.attachmentInfoMediaType)}>{data.mediaType}</span>}
		</div>
	);
};

// ============================================================================
// AttachmentRemove - Remove button
// ============================================================================

export type AttachmentRemoveProps = ComponentProps<typeof Button> & {
	label?: string;
};

export const AttachmentRemove = ({ label = 'Remove', styles = [], children, ...props }: AttachmentRemoveProps) => {
	const { onRemove, variant } = useAttachmentContext();

	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onRemove?.();
		},
		[onRemove]
	);

	if (!onRemove) {
		return null;
	}

	return (
		<Button
			aria-label={label}
			styles={[
				s.attachmentRemoveButton,
				variant === 'grid' && s.attachmentRemoveButtonGrid,
				variant === 'inline' && s.attachmentRemoveButtonInline,
				variant === 'list' && s.attachmentRemoveButtonList,
				...styles,
			]}
			onClick={handleClick}
			type='button'
			variant='ghost'
			{...props}
		>
			{children ?? <XIconNew />}
			<span className={css(s.attachmentRemoveButtonLabel)}>{label}</span>
		</Button>
	);
};

// ============================================================================
// AttachmentHoverCard - Hover preview
// ============================================================================

export type AttachmentHoverCardProps = ComponentProps<typeof HoverCard>;

export const AttachmentHoverCard = ({ openDelay = 0, closeDelay = 0, ...props }: AttachmentHoverCardProps) => (
	<HoverCard closeDelay={closeDelay} openDelay={openDelay} {...props} />
);

export type AttachmentHoverCardTriggerProps = ComponentProps<typeof HoverCardTrigger>;

export const AttachmentHoverCardTrigger = (props: AttachmentHoverCardTriggerProps) => <HoverCardTrigger {...props} />;

export type AttachmentHoverCardContentProps = ComponentProps<typeof HoverCardContent>;

export const AttachmentHoverCardContent = ({
	align = 'start',
	styles = [],
	...props
}: AttachmentHoverCardContentProps) => (
	<HoverCardContent align={align} styles={[s.attachmentHoverCardContent, ...styles]} {...props} />
);

// ============================================================================
// AttachmentEmpty - Empty state
// ============================================================================

export type AttachmentEmptyProps = AphroditeStyled<HTMLAttributes<HTMLDivElement>>;

export const AttachmentEmpty = ({ styles = [], children, ...props }: AttachmentEmptyProps) => (
	<div className={css(s.attachmentEmpty, ...styles)} {...props}>
		{children ?? 'No attachments'}
	</div>
);

const s = StyleSheet.create({
	attachmentImage: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
	attachmentImageRounded: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		borderRadius: 4,
	},
	attachments: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 8,
	},
	attachmentsList: {
		flexDirection: 'column',
	},
	attachmentsNonList: {
		flexWrap: 'wrap',
	},
	attachmentsGrid: {
		marginLeft: 'auto',
		width: 'fit-content',
	},
	attachment: {
		boxSizing: 'border-box',
		position: 'relative',
		minWidth: 0,
		maxWidth: '100%',
		'--attachment-button-opacity': '0',
		':hover': {
			'--attachment-button-opacity': '1',
		},
	},
	attachmentGrid: {
		width: 96,
		height: 96,
		overflow: 'hidden',
		borderRadius: 8,
	},
	attachmentInline: {
		display: 'flex',
		height: 32,
		cursor: 'pointer',
		userSelect: 'none',
		alignItems: 'center',
		gap: 6,
		borderRadius: 6,
		border: `1px solid ${inputBorderColor}`,
		paddingLeft: 6,
		paddingRight: 6,
		fontSize: 12,
		transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
		':hover': {
			backgroundColor: background,
		},
	},
	attachmentList: {
		display: 'flex',
		width: '100%',
		alignItems: 'center',
		gap: 12,
		borderRadius: 8,
		border: `1px solid ${inputBorderColor}`,
		padding: 12,
		':hover': {
			backgroundColor: background,
		},
	},
	attachmentPreviewIcon: {
		color: darkGrayFontColor,
	},
	attachmentPreviewIconInline: {
		width: 12,
		height: 12,
	},
	attachmentPreviewIconNonInline: {
		width: 16,
		height: 16,
	},
	attachmentVideo: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
	attachmentPreview: {
		display: 'flex',
		flexShrink: 0,
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		boxSizing: 'border-box',
	},
	attachmentPreviewGrid: {
		width: '100%',
		height: '100%',
		backgroundColor: antiFlashWhite,
	},
	attachmentPreviewInline: {
		width: 20,
		height: 20,
		borderRadius: 4,
		backgroundColor: white,
	},
	attachmentPreviewList: {
		width: 48,
		height: 48,
		borderRadius: 8,
		backgroundColor: antiFlashWhite,
	},
	attachmentInfo: {
		minWidth: 0,
		flex: 1,
	},
	attachmentInfoLabel: {
		color: titles,
		display: 'block',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	attachmentInfoMediaType: {
		display: 'block',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: darkGrayFontColor,
		fontSize: 12,
		lineHeight: 1.2,
	},
	attachmentRemoveButton: {
		boxSizing: 'border-box',
	},
	attachmentRemoveButtonGrid: {
		position: 'absolute',
		top: 8,
		right: 8,
		width: 24,
		height: 24,
		borderRadius: 9999,
		padding: 0,
		backgroundColor: 'rgba(255, 255, 255, 0.8)',
		backdropFilter: 'blur(8px)',
		// @ts-ignore
		opacity: 'var(--attachment-button-opacity)',
		transition: 'opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
		':hover': {
			backgroundColor: white,
		},
		':has(*) > svg': {
			width: 12,
			height: 12,
		},
	},
	attachmentRemoveButtonInline: {
		width: 20,
		height: 20,
		borderRadius: 4,
		padding: 0,
		// @ts-ignore
		opacity: 'var(--attachment-button-opacity)',
		transition: 'opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
		':has(*) > svg': {
			width: 16,
			height: 16,
		},
	},
	attachmentRemoveButtonList: {
		width: 32,
		height: 32,
		flexShrink: 0,
		borderRadius: 8,
		padding: 0,
		':has(*) > svg': {
			width: 16,
			height: 16,
		},
	},
	attachmentRemoveButtonLabel: {
		position: 'absolute',
		width: 1,
		height: 1,
		padding: 0,
		margin: -1,
		overflow: 'hidden',
		clip: 'rect(0, 0, 0, 0)',
		whiteSpace: 'nowrap',
		borderWidth: 0,
	},
	attachmentHoverCardContent: {
		width: 'auto',
		padding: 8,
	},
	attachmentEmpty: {
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		padding: 16,
		color: darkGrayFontColor,
		fontSize: 12,
	},
});
